import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PINEntry from './PINEntry';
import { useWallet } from '../../contexts/WalletContext';

const QRScan = ({ onBack, onSuccess, t, isRTL }) => {
  const [scanned, setScanned] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [simoId, setSimoId] = useState('');
  const [amount, setAmount] = useState('');
  const [step, setStep] = useState('scan'); // 'scan' | 'manual' | 'amount' | 'pin'
  const { payQR } = useWallet();

  // Mock user data
  const mockUser = {
    name: 'Hassan Benali',
    simoId: 'hassan.benali@simopay',
    color: '#DC2626',
  };

  const tr = t || {
    scanQR: 'Scan QR Code', pointCamera: "Point at payment code",
    simulateScan: 'Simulate Scan (Demo)', readyToScan: 'Ready to scan',
    alignQR: 'Align the QR code within the frame',
    enterManually: 'Enter SimoPay ID Manually',
    enterSimoId: 'Enter SimoPay ID',
    enterAmount: 'Enter Amount',
    payNow: 'Pay Now',
    backToScan: 'Back to Scan',
  };

  const handleSimulateScan = () => {
    setScanned(true);
    // Simulate going to PIN entry after scan
    setTimeout(() => {
      setAmount('75');
      setStep('pin');
    }, 900);
  };

  const handleManualEntry = () => {
    if (!simoId || !simoId.includes('@simopay')) return;
    setStep('amount');
  };

  const handlePayment = () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setStep('pin');
  };

  const handlePINSuccess = () => {
    setTimeout(() => {
      if (scanned) {
        // Merchant payment from simulated QR scan — use translation key for "Café Atlas"
        payQR('75', { kind: 'merchant', nameKey: 'txCafeAtlas' });
        const toastMsg = typeof tr.toastQRSuccess === 'function'
          ? tr.toastQRSuccess('75')
          : 'QR scanned! Café Atlas — 75 MAD payment confirmed.';
        onSuccess(toastMsg);
      } else {
        // Manual SimoPay ID payment
        const target = simoId || mockUser.simoId;
        payQR(amount, { kind: 'contact', simoId: target, name: mockUser.name });
        const toastMsg = typeof tr.toastPaySuccess === 'function'
          ? tr.toastPaySuccess(amount, target)
          : `Paid ${amount} MAD to ${target}`;
        onSuccess(toastMsg);
      }
    }, 400);
  };

  const getAmountFontSize = (val) => {
    const len = String(val || '').length;
    if (len > 7) return '26px';
    if (len > 5) return '32px';
    if (len > 3) return '38px';
    return '42px';
  };

  return (
    <div
      data-testid="qrscan-screen"
      className="no-scrollbar"
      style={{
        background: '#0F0F0F',
        height: '100%',
        fontFamily: 'Inter, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        paddingBottom: '12px',
        direction: isRTL ? 'rtl' : 'ltr',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px' }}>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={step === 'amount' ? () => setStep('manual') : onBack}
          style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#F5F1EA', fontSize: '16px', flexShrink: 0 }}
        >
          {isRTL ? '→' : '←'}
        </motion.button>
        <h2 style={{ color: '#F5F1EA', fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.4px', flex: 1, marginLeft: '12px' }}>{tr.scanQRTitle || tr.scanQR}</h2>
        
        {/* Simulate button on top right (only in scan mode) */}
        {step === 'scan' && (
          <motion.button
            data-testid="simulate-scan-btn"
            whileTap={{ scale: 0.95 }}
            onClick={handleSimulateScan}
            style={{ 
              background: 'rgba(212,175,55,0.12)', 
              border: '1px solid rgba(212,175,55,0.25)', 
              borderRadius: '10px', 
              padding: '8px 14px', 
              color: '#D4AF37', 
              fontSize: '12px', 
              fontWeight: '600', 
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {tr.simulateScan || 'Simulate'}
          </motion.button>
        )}
      </div>

      {step === 'scan' && (
        <div style={{ padding: '0 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Scanner - larger and centered */}
          <div
            style={{ 
              position: 'relative', 
              width: '100%', 
              aspectRatio: '1', 
              maxHeight: '280px', 
              borderRadius: '24px', 
              background: 'rgba(255,255,255,0.02)', 
              border: '1px solid rgba(255,255,255,0.06)', 
              overflow: 'hidden', 
              margin: '20px auto 24px',
              maxWidth: '280px'
            }}
          >
            {[
              { top: 14, left: 14, bt: true, bl: true },
              { top: 14, right: 14, bt: true, br: true },
              { bottom: 14, left: 14, bb: true, bl: true },
              { bottom: 14, right: 14, bb: true, br: true },
            ].map((pos, i) => (
              <div key={i} style={{ position: 'absolute', width: '28px', height: '28px', top: pos.top, right: pos.right, bottom: pos.bottom, left: pos.left, borderTop: pos.bt ? '3px solid #D4AF37' : 'none', borderBottom: pos.bb ? '3px solid #D4AF37' : 'none', borderLeft: pos.bl ? '3px solid #D4AF37' : 'none', borderRight: pos.br ? '3px solid #D4AF37' : 'none', borderRadius: i === 0 ? '6px 0 0 0' : i === 1 ? '0 6px 0 0' : i === 2 ? '0 0 0 6px' : '0 0 6px 0' }} />
            ))}
            {!scanned && (
              <motion.div
                animate={{ y: [0, 240, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                style={{ position: 'absolute', left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.9), transparent)', boxShadow: '0 0 10px rgba(212,175,55,0.6)', top: 0 }}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '10px' }}>
              {scanned ? (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(74,222,128,0.15)', border: '2px solid #4ADE80', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', color: '#4ADE80' }}>✓</motion.div>
              ) : (
                <span style={{ fontSize: '36px', opacity: 0.15 }}>⊡</span>
              )}
            </div>
          </div>

          {/* Status card */}
          <div style={{ background: scanned ? 'rgba(74,222,128,0.06)' : 'rgba(212,175,55,0.05)', border: scanned ? '1px solid rgba(74,222,128,0.15)' : '1px solid rgba(212,175,55,0.12)', borderRadius: '16px', padding: '16px', textAlign: 'center', marginBottom: '16px' }}>
            <p style={{ color: scanned ? '#4ADE80' : '#D4AF37', fontSize: '13px', fontWeight: '600', margin: 0 }}>
              {scanned ? '✓ QR Detected' : tr.readyToScan}
            </p>
          </div>

          {/* Pay Manually button */}
          {!scanned && (
            <motion.button
              data-testid="manual-entry-btn"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setStep('manual')}
              style={{ 
                width: '100%', 
                background: 'rgba(255,255,255,0.04)', 
                color: '#F5F1EA', 
                border: '1px solid rgba(255,255,255,0.1)', 
                borderRadius: '16px', 
                padding: '16px', 
                fontSize: '15px', 
                fontWeight: '600', 
                cursor: 'pointer',
                marginTop: 'auto'
              }}
            >
              {tr.payManually || 'Pay Manually'}
            </motion.button>
          )}
        </div>
      )}

      {step === 'manual' && (
        <div style={{ padding: '0 20px', flex: 1 }}>
          <p style={{ color: '#8B93A7', fontSize: '12px', marginBottom: '20px' }}>{tr.enterSimoId}</p>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '14px',
              padding: '12px 14px',
              marginBottom: '20px',
            }}
          >
            <input
              data-testid="manual-simoid-input"
              type="text"
              value={simoId}
              onChange={(e) => setSimoId(e.target.value)}
              placeholder="username@simopay"
              style={{
                background: 'none',
                border: 'none',
                outline: 'none',
                color: '#F5F1EA',
                fontSize: '14px',
                flex: 1,
                fontFamily: 'Inter, sans-serif',
              }}
            />
          </div>

          {simoId && simoId.includes('@simopay') && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '16px', marginBottom: '20px' }}
            >
              <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: mockUser.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>{mockUser.name[0]}</div>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#F5F1EA', fontSize: '14px', fontWeight: '600', margin: 0 }}>{mockUser.name}</p>
                <p style={{ color: '#D4AF37', fontSize: '12px', margin: '2px 0 0', fontWeight: '500' }}>{mockUser.simoId}</p>
              </div>
            </motion.div>
          )}

          <motion.button
            data-testid="manual-continue-btn"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleManualEntry}
            disabled={!simoId || !simoId.includes('@simopay')}
            style={{ width: '100%', background: simoId && simoId.includes('@simopay') ? 'linear-gradient(135deg, #D4AF37, #B8962E)' : 'rgba(255,255,255,0.06)', color: simoId && simoId.includes('@simopay') ? '#0F0F0F' : '#8B93A7', border: 'none', borderRadius: '16px', padding: '15px', fontSize: '14px', fontWeight: '700', cursor: simoId && simoId.includes('@simopay') ? 'pointer' : 'not-allowed', transition: 'all 0.25s' }}
          >
            Continue
          </motion.button>
        </div>
      )}

      {step === 'amount' && (
        <div style={{ padding: '0 20px', flex: 1 }}>
          {/* Recipient info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '16px', marginBottom: '24px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: mockUser.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>{mockUser.name[0]}</div>
            <div style={{ flex: 1 }}>
              <p style={{ color: '#F5F1EA', fontSize: '14px', fontWeight: '600', margin: 0 }}>{mockUser.name}</p>
              <p style={{ color: '#D4AF37', fontSize: '12px', margin: '2px 0 0', fontWeight: '500' }}>{mockUser.simoId}</p>
            </div>
          </div>

          {/* Amount input — fixed short golden line, amount centered above with currency on right */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '32px' }}>
            <p style={{ color: '#8B93A7', fontSize: '11px', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600' }}>{tr.enterAmount}</p>

            <label
              htmlFor="qr-amount-input"
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                gap: '10px',
                height: '56px',
                cursor: 'text',
                width: '100%',
                maxWidth: '280px',
              }}
            >
              <input
                id="qr-amount-input"
                data-testid="qr-amount-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={amount}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  if (val.length <= 7) setAmount(val);
                }}
                placeholder="0"
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  color: amount ? '#F5F1EA' : '#5C6478',
                  fontSize: getAmountFontSize(amount || '0'),
                  fontWeight: '800',
                  letterSpacing: '-1.5px',
                  textAlign: 'right',
                  width: 'auto',
                  minWidth: '40px',
                  maxWidth: '180px',
                  fontFamily: 'Inter, sans-serif',
                  transition: 'font-size 0.15s ease, color 0.2s ease',
                  padding: 0,
                }}
                size={Math.max(1, (amount || '0').length)}
              />
              <span style={{ color: '#8B93A7', fontSize: '17px', fontWeight: '500', flexShrink: 0 }}>MAD</span>
            </label>

            {/* Constant short golden line */}
            <div
              style={{
                width: '88px',
                height: '2px',
                background: 'linear-gradient(90deg, rgba(212,175,55,0.15), rgba(212,175,55,0.85), rgba(212,175,55,0.15))',
                borderRadius: '2px',
                marginTop: '6px',
                boxShadow: '0 0 12px rgba(212,175,55,0.35)',
              }}
            />
          </div>

          {/* Quick amounts */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '28px' }}>
            {[50, 100, 250, 500].map((val) => (
              <motion.button
                key={val}
                whileTap={{ scale: 0.93 }}
                onClick={() => setAmount(String(val))}
                style={{ padding: '6px 12px', borderRadius: '100px', background: amount === String(val) ? 'rgba(212,175,55,0.12)' : 'rgba(255,255,255,0.04)', border: amount === String(val) ? '1px solid rgba(212,175,55,0.25)' : '1px solid rgba(255,255,255,0.07)', color: amount === String(val) ? '#D4AF37' : '#8B93A7', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
              >
                {val}
              </motion.button>
            ))}
          </div>

          <motion.button
            data-testid="qr-pay-btn"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={handlePayment}
            disabled={!amount || parseFloat(amount) <= 0}
            style={{ width: '100%', background: amount && parseFloat(amount) > 0 ? 'linear-gradient(135deg, #D4AF37, #B8962E)' : 'rgba(255,255,255,0.06)', color: amount && parseFloat(amount) > 0 ? '#0F0F0F' : '#8B93A7', border: 'none', borderRadius: '16px', padding: '15px', fontSize: '15px', fontWeight: '700', cursor: amount ? 'pointer' : 'not-allowed', transition: 'all 0.25s' }}
          >
            {tr.payNow}{amount ? ` → ${amount} MAD` : ''}
          </motion.button>
        </div>
      )}

      {/* PIN Entry — full screen change */}
      {step === 'pin' && (
        <PINEntry
          amount={amount}
          recipient={scanned ? 'Café Atlas' : mockUser.name}
          mode="pay"
          onSuccess={handlePINSuccess}
          onCancel={() => setStep(scanned ? 'scan' : 'amount')}
          t={t}
          isRTL={isRTL}
        />
      )}
    </div>
  );
};

export default QRScan;
